import React from "react";
import HeroSection from "../components/HeroSection";
import { heroSections } from "../constants/data.ts";
import images from "../constants/images.ts";

const marketingServices = [
  {
    title: "Search Engine Optimization (SEO)",
    description:
      "Improve your website's visibility on search engines and attract high-quality organic traffic with our comprehensive SEO solutions.",
    listTitle: "Our SEO Services Include:",
    image: images.digitalMarketingServices.seo,
    items: [
      "Website SEO Audit",
      "Keyword Research & Strategy",
      "On-Page SEO Optimization",
      "Technical SEO",
      "Local SEO",
      "E-commerce SEO",
      "Link Building",
      "Content Optimization",
      "SEO Performance Monitoring",
      "Monthly SEO Reports",
    ],
    benefits: [
      "Higher Google Rankings",
      "Increased Organic Traffic",
      "Better User Experience",
      "Higher Conversion Rates",
      "Long-Term Business Growth",
    ],
  },
  {
    title: "Social Media Marketing",
    description:
      "Build meaningful connections with your audience and strengthen your brand across major social media platforms.",
    listTitle: "Our Social Media Services Include:",
    image: images.digitalMarketingServices.socialMediaMarketing,
    items: [
      "Social Media Strategy",
      "Content Planning & Creation",
      "Creative Post Designs",
      "Page Management",
      "Community Engagement",
      "Paid Social Advertising",
      "Influencer Campaign Support",
      "Analytics & Performance Reports",
    ],
    benefits: [
      "Increased Brand Awareness",
      "Better Customer Engagement",
      "Improved Brand Loyalty",
      "Higher Website Traffic",
      "More Sales Opportunities",
    ],
  },
  {
    title: "Performance Marketing",
    description:
      "Maximize your marketing budget with ROI-focused advertising campaigns designed to generate measurable business results.",
    listTitle: "Our Performance Marketing Services:",
    image: images.digitalMarketingServices.performanceMarketing,
    items: [
      "Google Ads Campaigns",
      "Meta (Facebook & Instagram) Ads",
      "LinkedIn Advertising",
      "YouTube Ads",
      "Display Advertising",
      "Remarketing Campaigns",
      "Conversion Rate Optimization",
      "Landing Page Optimization",
      "Campaign Analytics",
    ],
    benefits: [
      "Qualified Lead Generation",
      "Lower Cost Per Acquisition",
      "Higher Return on Ad Spend (ROAS)",
      "Faster Business Growth",
      "Real-Time Campaign Optimization",
    ],
  },
  {
    title: "Content Marketing",
    description:
      "Deliver valuable content that educates, engages, and converts your target audience while strengthening your brand authority.",
    listTitle: "Our Content Marketing Services:",
    image: images.digitalMarketingServices.contentMarketing,
    items: [
      "Content Strategy",
      "Blog Writing",
      "Website Content",
      "SEO Content Creation",
      "Email Marketing Content",
      "Case Studies",
      "Whitepapers",
      "Product Descriptions",
      "Video Script Writing",
      "Social Media Content",
    ],
    benefits: [
      "Build Brand Authority",
      "Improve Search Rankings",
      "Increase Website Traffic",
      "Generate Qualified Leads",
      "Enhance Customer Trust",
    ],
  },
  {
    title: "Branding Solutions",
    description:
      "Create a powerful and memorable brand identity that distinguishes your business from competitors.",
    listTitle: "Our Branding Services:",
    image: images.digitalMarketingServices.brandingSolutions,
    items: [
      "Brand Strategy",
      "Logo Design",
      "Brand Identity Design",
      "Brand Guidelines",
      "Corporate Branding",
      "Marketing Collateral Design",
      "Packaging Design",
      "Brand Messaging",
      "Visual Identity Development",
      "Rebranding Services",
    ],
    benefits: [
      "Strong Brand Recognition",
      "Consistent Brand Image",
      "Increased Customer Trust",
      "Competitive Market Position",
      "Long-Term Brand Value",
    ],
  },
  {
    title: "Lead Generation",
    description:
      "Generate high-quality leads that convert into loyal customers using proven digital marketing techniques.",
    listTitle: "Our Lead Generation Services:",
    image: images.digitalMarketingServices.leadGeneration,
    items: [
      "Landing Page Development",
      "PPC Lead Campaigns",
      "Social Media Lead Campaigns",
      "Lead Funnels",
      "Email Marketing",
      "Marketing Automation",
      "CRM Integration",
      "Lead Nurturing Campaigns",
      "Conversion Optimization",
      "Analytics & Reporting",
    ],
    benefits: [
      "Qualified Business Leads",
      "Higher Conversion Rates",
      "Increased Sales Opportunities",
      "Lower Customer Acquisition Cost",
      "Sustainable Business Growth",
    ],
  },
];

const platforms = [
  "Facebook",
  "Instagram",
  "LinkedIn",
  "X (Twitter)",
  "YouTube",
  "Pinterest",
];

const reasons = [
  "Customized Digital Marketing Strategies",
  "Experienced Marketing Professionals",
  "Data-Driven Decision Making",
  "Transparent Reporting",
  "ROI-Focused Campaigns",
  "Latest Marketing Tools & Technologies",
  "Dedicated Account Management",
  "Affordable Pricing",
  "Scalable Marketing Solutions",
  "Continuous Performance Optimization",
];

const industries = [
  "Healthcare",
  "Education",
  "E-commerce",
  "Real Estate",
  "Manufacturing",
  "Finance",
  "Travel & Tourism",
  "IT & Software",
  "Retail",
  "Logistics",
  "Startups",
  "Professional Services",
];

const DigitalMarketing = () => {
  return (
    <main className="bg-background">
      <HeroSection {...heroSections.digitalMarketing} />

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl">
          <p className="mb-3 heading-main text-primary">Digital Marketing</p>
          <h2 className="heading-main mb-5">
            Grow Your Brand with Data-Driven Digital Marketing
          </h2>
          <div className="space-y-4">
            <p className="text-body">
              In today's competitive digital landscape, having a website alone
              isn't enough. Your business needs a strong online presence that
              attracts the right audience, builds trust, and converts visitors
              into customers.
            </p>
            <p className="text-body">
              At Westlisoft, we create customized digital marketing strategies
              that help businesses increase visibility, generate qualified
              leads, improve customer engagement, and maximize return on
              investment. Our team combines creativity, technology, and
              analytics to deliver measurable business growth.
            </p>
            <p className="text-body">
              Whether you're a startup, SME, or enterprise, we provide
              end-to-end digital marketing solutions tailored to your business
              goals.
            </p>
          </div>
        </div>
      </section>

      <section className="px-outer py-16 md:pb-24">
        <div className="mx-auto w-full max-w-6xl">
          <div className="mb-12 max-w-3xl">
            <p className="mb-3 heading-main text-primary">
              Our Digital Marketing Services
            </p>
          </div>

          <div className="space-y-10 md:space-y-14">
            {marketingServices.map((service, index) => {
              const imageFirst = index % 2 === 1;

              return (
                <article
                  key={service.title}
                  className="grid items-center gap-6 rounded-card bg-white p-5 shadow-sm md:grid-cols-2 md:gap-10 lg:p-8"
                >
                  <div
                    className={
                      imageFirst
                        ? "order-2 md:order-2"
                        : "order-2 md:order-1"
                    }
                  >
                    <h3 className="mb-4 font-rubik text-2xl font-bold text-secondary md:text-3xl">
                      {service.title}
                    </h3>
                    <p className="text-body mb-5">{service.description}</p>
                    <h4 className="mb-3 font-rubik text-lg font-semibold text-secondary">
                      {service.listTitle}
                    </h4>
                    <ul className="mb-5 grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                      {service.items.map((item) => (
                        <li key={item} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                    <h4 className="mb-3 font-rubik text-lg font-semibold text-secondary">
                      Benefits
                    </h4>
                    <ul className="grid gap-2 text-sm font-light text-gray-700 sm:grid-cols-2">
                      {service.benefits.map((benefit) => (
                        <li key={benefit} className="flex gap-2">
                          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                          <span>{benefit}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div
                    className={
                      imageFirst
                        ? "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-1 md:h-[320px] lg:h-[360px]"
                        : "order-1 flex h-60 items-center justify-center rounded-card bg-gray-50 p-3 sm:h-72 md:order-2 md:h-[320px] lg:h-[360px]"
                    }
                  >
                    <img
                      src={service.image}
                      alt={service.title}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">Platforms We Manage</p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We help brands build consistent communication and performance
            across major social media and content platforms.
          </p>
          <ul className="mx-auto grid max-w-4xl gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {platforms.map((platform) => (
              <li
                key={platform}
                className="flex min-h-16 items-center justify-center rounded-card bg-background px-4 py-4 text-center font-rubik text-sm font-light text-gray-700"
              >
                {platform}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <h2 className="heading-main mb-8 text-primary">
            Why Choose Westlisoft?
          </h2>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-5">
            {reasons.map((reason) => (
              <li
                key={reason}
                className="flex min-h-20 items-center justify-center rounded-card bg-white px-4 py-4 text-center font-rubik text-sm font-light text-gray-700 shadow-sm"
              >
                {reason}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-white px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-6xl text-center">
          <p className="mb-4 heading-main text-primary">
            Industries We Serve
          </p>
          <p className="text-body mx-auto mb-8 max-w-3xl">
            We provide digital marketing services for businesses across various
            industries, including:
          </p>
          <ul className="mx-auto grid max-w-5xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {industries.map((industry) => (
              <li
                key={industry}
                className="flex min-h-16 items-center justify-center rounded-card bg-background px-4 py-4 text-center font-rubik text-sm font-light text-gray-700"
              >
                {industry}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="px-outer py-16 md:py-24">
        <div className="mx-auto w-full max-w-5xl text-center">
          <h2 className="heading-main mb-4 text-primary">
            Ready to Grow Your Business?
          </h2>
          <p className="text-body mx-auto max-w-4xl">
            Partner with Westlisoft to transform your online presence into a
            powerful growth engine. From increasing website traffic and building
            brand awareness to generating qualified leads and maximizing ROI,
            our digital marketing experts are committed to helping your
            business achieve sustainable success.
          </p>
        </div>
      </section>
    </main>
  );
};

export default DigitalMarketing;
